// src/services/paymentService.js
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';

/** The manual UPI provider key on the server ("Payments:Providers:manual-upi"). */
export const MANUAL_UPI = 'manual-upi';

/** UPI transaction id (UTR) as accepted by the server. */
export const UTR_PATTERN = /^[A-Za-z0-9]{10,22}$/;

// Unwraps ApiResponse<T> ({ success, message, data }).
const dataOf = response => response?.data?.data;

const paymentService = {
  // ---------- Admin (kept in the { success, data, error } shape the admin pages expect) ----------

  // Get all transactions with filters and pagination
  getTransactions: async (filters = {}) => {
    try {
      const params = {};

      if (filters.userId) params.UserId = filters.userId;
      if (filters.orderId) params.OrderId = filters.orderId;
      if (filters.transactionId) params.TransactionId = filters.transactionId;
      if (filters.paymentMethod) params.PaymentMethod = filters.paymentMethod;
      if (filters.status) params.Status = filters.status;
      if (filters.startDate) params.StartDate = filters.startDate;
      if (filters.endDate) params.EndDate = filters.endDate;
      if (filters.minAmount) params.MinAmount = filters.minAmount;
      if (filters.maxAmount) params.MaxAmount = filters.maxAmount;
      if (filters.isFullPaymentDone !== undefined)
        params.IsFullPaymentDone = filters.isFullPaymentDone;
      if (filters.page) params.Page = filters.page;
      if (filters.pageSize) params.PageSize = filters.pageSize;

      const response = await apiClient.get(
        API_ENDPOINTS.PAYMENT.GET_TRANSACTIONS,
        { params }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || 'Failed to fetch transactions',
      };
    }
  },

  // Get payment by order ID
  getPaymentByOrderId: async orderId => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.PAYMENT.GET_BY_ORDER(orderId)
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || 'Failed to fetch payment',
      };
    }
  },

  // ---------- Checkout (these return the payload and throw on failure; use getErrorMessage) ----------

  /** What checkout must collect: { requireTermsAcceptance, termsVersion, requireBuyerState, states: [{code, name}] }. */
  getCheckoutSettings: async () =>
    dataOf(await apiClient.get(API_ENDPOINTS.CHECKOUT.SETTINGS)),

  /** Methods enabled on the server: [{ key, displayName, flow: 'Manual'|'Redirect', isDefault }]. */
  getPaymentMethods: async () =>
    dataOf(await apiClient.get(API_ENDPOINTS.PAYMENTS.METHODS)) || [],

  /** Server-calculated price: { unitPrice, platformFee, taxAmount, taxRatePercent, pricesIncludeTax, taxLabel, total, licenseTerms }. */
  getQuote: async productId =>
    dataOf(await apiClient.get(API_ENDPOINTS.CHECKOUT.QUOTE(productId))),

  /** The product being bought (name, media, edition) for the checkout summary. */
  getProduct: async productId =>
    dataOf(await apiClient.get(API_ENDPOINTS.PRODUCTS.GET_BY_ID(productId))),

  /**
   * POST /api/checkout. Creates the order (or reuses the buyer's open one for the same product)
   * and starts a payment attempt. Returns OrderCreateResponse:
   * { orderId, paymentId, paymentMethod, paymentFlow, paymentRedirectUrl, paymentInstructions, orderDetails }.
   */
  createOrder: async ({
    productId,
    paymentMethod,
    orderNotes,
    acceptedTerms,
    buyerStateCode,
    buyerGstin,
    buyerBusinessName,
  }) =>
    dataOf(
      await apiClient.post(API_ENDPOINTS.CHECKOUT.CREATE, {
        productId,
        paymentMethod,
        orderNotes: orderNotes || null,
        acceptedTerms: acceptedTerms === true,
        buyerStateCode: buyerStateCode || null,
        buyerGstin: buyerGstin || null,
        buyerBusinessName: buyerBusinessName || null,
      })
    ),

  /**
   * POST /api/checkout/{orderId}/pay. Resumes (or starts) the payment for an order; safe on every page load.
   * Same OrderCreateResponse as createOrder. orderDetails.state tells what to show:
   * PendingPayment | PaymentFailed (payable) | PaymentSubmitted | Paid | Fulfilled | Cancelled | Refunded.
   */
  startPayment: async (orderId, paymentMethod = MANUAL_UPI) =>
    dataOf(
      await apiClient.post(API_ENDPOINTS.CHECKOUT.PAY(encodeURIComponent(orderId)), {
        paymentMethod,
      })
    ),

  /** Buyer submits the UPI transaction id (UTR) for a manual payment. Returns { outcome, paymentState, orderState }. */
  submitPaymentProof: async (paymentId, referenceId) =>
    dataOf(
      await apiClient.post(API_ENDPOINTS.PAYMENTS.SUBMIT_PROOF(encodeURIComponent(paymentId)), {
        referenceId,
      })
    ),

  /** "SUCCESS" | "PENDING" | "FAILED" for an order (re-checks redirect providers with their API). */
  getOrderPaymentStatus: async orderId =>
    dataOf(
      await apiClient.get(API_ENDPOINTS.ORDERS.GET_PAYMENT_STATUS(encodeURIComponent(orderId)))
    ),
};

export default paymentService;
